/**
 * useDiscussionElements.ts — PARCOURS lot 1 (Écran)
 * Appelle GET /api/v1/discussions/{external_id}/elements pour obtenir
 * les éléments du greffier groupés par famille.
 *
 * Déclencheurs identiques à useDiscussionEtapes :
 *   (a) ouverture / changement de discussion (activeThreadId)
 *   (b) fin de réponse de Carlos (isTyping passe de true à false)
 *   (c) 20 s après (b) — le temps que le greffier range l'échange
 *
 * Sans réponse de la route : retourne null (fallback ancien comportement).
 */

import { useState, useEffect, useRef, useCallback } from "react";

export interface ElementSujet {
  id: number;
  objectif: string;
  tension: string;
  resultat_vise: string;
  /** B1.MAQUETTE.E1 G1/G2 — renvoyés par /elements depuis M1 */
  echeance?: string;
  nature?: string;
  confirme: boolean;
  /** DEC-SUJET-MUR-1 : sujet mûr (critère B, jugé par le serveur) ; non mûr : « En cours de précision », sans bouton Confirmer */
  mur?: boolean;
  version: number;
  avis: ElementAvis[];
}

export interface ElementFait {
  id: number;
  enonce: string;
  valeur: number | null;
  unite: string | null;
  provenance: string;
  etat: string;
  a_verifier: boolean;
  avis: ElementAvis[];
  /** DEC-GREFFIER-AGENT-3 (règle D) : « profil » (l'entreprise, marqué pour le Blueprint) ou « probleme » ; absent sur l'ancien greffier */
  categorie?: string;
  pour_blueprint?: boolean;
}

export interface ElementQuestion {
  id: number;
  question: string;
  statut: string;
  lien_fait_reponse: number | null;
  avis: ElementAvis[];
}

export interface ElementAvis {
  id: number;
  bot: string;
  domaine: string;
  cible: string;
  points: string[];
  position: string;
  confiance: string;
}

export interface ElementPiste {
  id: number;
  titre: string;
  auteur: string;
  statut: string;
  rang: number | null;
  etat: string;
  avis: ElementAvis[];
}

export interface ElementDecision {
  id: number;
  enonce: string;
  etat: string;
  decide_par: string;
  pourquoi: string;
  date: string;
  avis: ElementAvis[];
  /** DEC-DECIDEUR-1 : acceptée en attente du décideur nommé ; question du décideur à poser (une fois par discussion) */
  en_attente_de?: string;
  question_decideur?: boolean;
}

/** DEC-GREFFIER-AGENT-3 (règle C) : un choix du geste en attente — route après /api/v1/discussions/{fil}/, corps, texte envoyé ensuite */
export interface ChoixGesteServeur {
  id: string;
  texte: string;
  /** S229 lot 7b : null pour une action de l'écran (« Voir le chantier », « Passer en Conception », « Rester dans la discussion ») */
  route: string | null;
  element_id: number;
  corps: Record<string, unknown>;
  demande_nom?: boolean;
  /** S229 lot 7b (trou 1) : l'option n'est pas envoyée comme message ; la bulle suivante vient du code */
  sans_message?: boolean;
  /** S231 lot Z1 : « aller_boite » (défile jusqu'à la boîte visée, ZQ2 a, ZQ4 a) et « focus_discussion » (curseur dans le champ, ZQ3 a) */
  action_ecran?: "voir_chantier" | "conception" | "rester" | "aller_boite" | "focus_discussion";
}

/** S231 lot Z1 (ZONES.CONNECTEES.1, DEC-HE2T-1 Q9 b) : la carte d'action du geste en attente (greffier.carte_du_geste) — titre, texte,
 *  boutons de la planche Z-GESTES, famille de la boîte visée (data-boite-greffier) et pastille du repère (P1 à P7, DEC-ZONES-GESTES-1) */
export interface CarteActionServeur {
  geste: string;
  element_id: number | null;
  titre: string;
  texte: string;
  boutons: ChoixGesteServeur[];
  boite: string;
  pastille: string;
  /** S231 lot D (DEC-ZONES-GESTES-3) : carte « Ce qu'on sait » — les faits du client « proposée » cochés d'avance par « Accepter » */
  faits?: number[];
}

/** Le geste en attente choisi par le code (greffier.geste_en_attente) ; null : les options restent celles de Carlos */
export interface GesteEnAttente {
  geste: string;
  element_id: number;
  question?: string;
  choix: ChoixGesteServeur[];
}

/** Relecture de /elements demandée après un geste (pilule du workspace ou option du chat) : les deux zones relisent */
export const EVT_RELIRE_ELEMENTS = "bt-relire-elements";

export interface ElementAction {
  id: number;
  quoi: string;
  executant: string;
  quand: string | null;
  etat: string;
}

export interface ElementDocument {
  id: number;
  titre: string;
  format: string;
  date: string;
  lien: string;
}

export interface PlanDeMatch {
  focus: ElementSujet | null;
  diagnostic: ElementFait[];
  decisions: ElementDecision[];
  actions: ElementAction[];
  a_verifier: Array<{ id: number; [k: string]: unknown }>;
  mis_de_cote: Array<{ id: number; [k: string]: unknown }>;
  valide: boolean;
  par?: number;
  date?: string;
  /** S229 lot 7b : le chantier créé depuis ce plan (« Créer le chantier ») ; null tant qu'il n'existe pas */
  chantier_id?: number | null;
}

// ── Pont types (bridge_discussion_cahier.py → format cahier) ────

export interface PontValeur {
  champ_id: string;
  valeur?: unknown;
  items?: Array<Record<string, unknown>>;
  structured_data?: Record<string, unknown>;
  niveau: string;
  source: string;
  provenance?: string;
  greffier_id?: number;
  block_id?: number;
  title?: string;
  summary?: string;
  source_bot?: string;
  source_bot_name?: string;
  credo_step?: string;
  created_at?: string;
}

export interface PontChamp {
  id: string;
  section: string;
  portee: string;
  libelle: string;
  types: string[];
  etapes: string[];
  source: string;
  qui: string;
  rapports: string[];
  note_rapports: string;
  ajout: string;
}

export interface PontSection {
  title: string;
  portee?: string;
}

export interface PontStructure {
  sections: Record<string, PontSection>;
  champs: PontChamp[];
}

export interface PontCouverture {
  remplis: number;
  total: number;
  statut: "couverte" | "en_cours" | "vide";
}

export interface DiscussionElements {
  ecran_greffier: boolean;
  a_elements: boolean;
  is_new_discussion?: boolean;
  couverture: Record<string, PontCouverture>;
  /** B1.MAQUETTE.E1 G11 — couverture par étape du greffier : objectif/situation/pistes/decisions/plan */
  couverture_etapes?: Record<string, "couverte" | "en_cours" | "vide">;
  etape_suggeree: string;
  sujet: (ElementSujet & { volets?: ElementSujet[] }) | null;
  /** ETAPES.AVANCEMENT.1 : nouveau sujet pas encore tranché par l'utilisateur (DEC-INCOHERENCE-SIGNALEE-1) */
  sujet_candidat?: ElementSujet | null;
  /** DEC-GREFFIER-AGENT-3 (règle B) : précision du sujet confirmé proposée par l'agent, à confirmer */
  sujet_precision?: (ElementSujet & { precision_de?: number }) | null;
  /** DEC-GREFFIER-AGENT-3 (règle C) */
  geste_en_attente?: GesteEnAttente | null;
  /** S229 lot 7d : le geste du plan (creer_chantier ou apres_chantier), rendu seul : ses options gardent leurs routes après un autre geste */
  geste_du_plan?: GesteEnAttente | null;
  /** TITRE.FIL.1 : titre rédigé par le serveur à partir du seul fil (chaîne vide tant qu'il n'est pas posé) */
  titre_fil?: string;
  faits: ElementFait[];
  questions: ElementQuestion[];
  pistes: ElementPiste[];
  decisions: ElementDecision[];
  actions: ElementAction[];
  avis_sans_cible: ElementAvis[];
  documents: ElementDocument[];
  plan_de_match: PlanDeMatch;
  structure?: PontStructure;
  valeurs?: PontValeur[];
  /** GREFFIER.AGENT.1 : état du rangement (« en_cours » pendant un passage de l'agent), mode, dernier passage (ids changés, résumé) */
  suivi_agent?: { rangement?: string | null; mode?: string; dernier_passage?: { resume?: string; ids?: number[]; quand?: string } | null;
    /** P-S232-31 n° 1 : un moment de la couche 3 demandé et pas encore joué (plan de match après Décider) ; l'écran relit toutes les 3 s */
    moment_en_cours?: boolean; moment_depuis?: string | null; moment_raisons?: string[] };
  /** S229 lot 7d : bulle de fin de plan écrite par le code (une fois, tant que le chantier n'existe pas) — id du message, texte, options */
  bulle_code?: { id: number; texte: string; options: string[]; gestes?: ChoixGesteServeur[] } | null;
  /** S231 lot Z1 : la carte d'action du geste en attente, une à la fois ; null : aucun geste n'attend Carl */
  carte_action?: CarteActionServeur | null;
}

/** Relectures de /elements après la fin d'une réponse (le greffier range l'échange en arrière-plan). */
const RELECTURES_APRES_REPONSE_MS = [20_000, 45_000, 90_000];
/** GREFFIER.AGENT.1 (réponse 4) : relecture pendant un passage de l'agent */
export const RELECTURE_EN_COURS_MS = 3_000;
/** S232 lot D6 : pendant cette fenêtre après la fin d'une réponse, l'écran relit toutes les 3 s jusqu'au passage du greffier */
export const FENETRE_RANGEMENT_MS = 20_000;

/** S232 lot D6 (preuve R-S232-10 étape 6, fil 3qeh : une seule lecture 4 s après « Je ne sais pas. », avant le rangement ; la relecture
 *  suivante à 20 s ; la boîte « Questions en suspens » plus ancienne que l'API) : relire dans 3 s tant que, dans les 20 s qui suivent la
 *  fin de la réponse, le passage du greffier de cet échange n'a pas eu lieu (dernier_passage.quand inchangé depuis la première lecture
 *  après la réponse) ou qu'il est en cours. Fonction pure (tests/test_s232_d6_relecture_js.py). */
export function relirePendantRangement(passageInitial: string | null, data: DiscussionElements, msDepuisFin: number): boolean {
  if (msDepuisFin >= FENETRE_RANGEMENT_MS) return false;
  const suivi = data.suivi_agent;
  if (suivi?.rangement === "en_cours") return true;
  return (suivi?.dernier_passage?.quand ?? null) === passageInitial;
}

/** P-S232-31 n° 1 (REVUE-NUIT-S232 ; banc A3 : le Plan de match arrivait 45 à 90 s après la décision) : tant que /elements dit qu'un moment
 *  est en cours (suivi_agent.moment_en_cours, levé par Décider / « Qui approuve : Oui »), relire toutes les 3 s, au plus BORNE_MOMENT_MS depuis
 *  la première lecture qui l'a vu. Fonction pure (tests/test_s232_p31_relecture_js.py). */
export const BORNE_MOMENT_MS = 90_000;
export function relirePendantMoment(data: DiscussionElements, msDepuisDebut: number): boolean {
  return !!data.suivi_agent?.moment_en_cours && msDepuisDebut < BORNE_MOMENT_MS;
}

/** S231 lot B (ps21 P2) : une seule lecture de /elements par fil pour tous les abonnés (carte d'action, workspace, fenêtre) — les lectures
 *  lancées en même temps se partagent la même requête, et la réponse est publiée à tous au même moment (carte et repère ensemble). */
type Abonne = (fil: string, data: DiscussionElements) => void;
const ABONNES = new Set<Abonne>();
const EN_VOL = new Map<string, Promise<{ ok: boolean; status: number; data: unknown }>>();
export function abonnerElements(a: Abonne): () => void { ABONNES.add(a); return () => { ABONNES.delete(a); }; }
export function publierElements(fil: string, data: DiscussionElements): void { for (const a of [...ABONNES]) a(fil, data); }
export function lecturePartagee(fil: string, lire: () => Promise<{ ok: boolean; status: number; data: unknown }>) {
  const deja = EN_VOL.get(fil);
  if (deja) return deja;
  const p = lire().finally(() => { EN_VOL.delete(fil); });
  EN_VOL.set(fil, p);
  return p;
}

/** S232 lot D1 (ELEMENTS.NAISSANCE.1, S232-PLAN-DEMO.md v1.2 section 3) : l'erreur à montrer après une lecture refusée de /elements.
 *  Un 404 sur un fil qui n'a encore jamais été lu avec succès est le fil neuf dont la discussion n'est pas encore créée (api_rest.py:13778,
 *  « Discussion introuvable », tant que la première réponse n'est pas enregistrée) : pas d'erreur, workspace vide, les relectures continuent.
 *  Tout autre code, ou un 404 sur un fil déjà lu, garde le cadre « Éléments : <code> ». Fonction pure (tests/test_s232_lot_d1_js.py). */
export function erreurDeLecture(status: number, filDejaLu: boolean): string | null {
  if (status === 404 && !filDejaLu) return null;
  return `Éléments : ${status}`;
}

export function useDiscussionElements(
  activeThreadId: string | null | undefined,
  isTyping: boolean,
): DiscussionElements | null {
  return useDiscussionElementsAvecErreur(activeThreadId, isTyping).elements;
}

/** B1.MAQUETTE.E1 (R-Q14) — même hook, avec l'état d'erreur du dernier chargement
 *  (null si le dernier chargement a réussi). */
export function useDiscussionElementsAvecErreur(
  activeThreadId: string | null | undefined,
  isTyping: boolean,
): { elements: DiscussionElements | null; erreur: string | null; recharger: () => void } {
  // DISC.ELEMENTS.FIL.1 (S226-DIAG-FIL-NEUF.md) : l'état porte son fil ; seuls les éléments du fil actif sont rendus — un fil neuf, ou un
  // fil qui rend 404, montre un workspace vide, jamais les éléments d'un autre fil (avant : ceux du fil précédent restaient affichés)
  const [etat, setEtat] = useState<{ fil: string; data: DiscussionElements } | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const prevTypingRef = useRef(isTyping);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const threadRef = useRef(activeThreadId);
  threadRef.current = activeThreadId;
  // S232 lot D1 : les fils déjà lus avec succès (un 404 sur l'un d'eux reste une erreur ; sur un fil neuf, ce n'en est pas une)
  const filsLusRef = useRef<Set<string>>(new Set());
  // S232 lot D6 : la fin de la dernière réponse (ms) et le passage du greffier vu à la première lecture qui la suit (undefined : pas encore lu)
  const finReponseRef = useRef<number | null>(null);
  const passageInitialRef = useRef<string | null | undefined>(undefined);
  // P-S232-31 n° 1 : début du moment en cours vu par l'écran (borne de 90 s) ; un seul minuteur de relecture rapide à la fois
  const debutMomentRef = useRef<number | null>(null);
  const relectureRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchElements = useCallback(async (externalId: string, _isRetry = false) => {
    try {
      const jwt = localStorage.getItem("ghostx-jwt");
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (jwt) headers["Authorization"] = `Bearer ${jwt}`;
      else {
        const key = (import.meta as unknown as Record<string, Record<string, string>>).env?.VITE_API_KEY;
        if (key) headers["X-API-Key"] = key;
      }
      const res = await lecturePartagee(externalId, async () => {
        const r = await fetch(`/api/v1/discussions/${externalId}/elements`, { headers });
        return { ok: r.ok, status: r.status, data: r.ok ? await r.json() : null };
      });
      if (!res.ok) {
        console.warn(`[useDiscussionElements] /elements ${res.status}`);
        if (threadRef.current === externalId) setErreur(erreurDeLecture(res.status, filsLusRef.current.has(externalId)));
        if (!_isRetry && threadRef.current === externalId) {
          setTimeout(() => { if (threadRef.current === externalId) fetchElements(externalId, true); }, 3_000);
        }
        return;
      }
      const data = res.data;
      filsLusRef.current.add(externalId);
      publierElements(externalId, data as DiscussionElements);  // S231 lot B : tous les abonnés du fil reçoivent la même réponse
      if (threadRef.current === externalId) {
        setErreur(null);
        // GREFFIER.AGENT.1 (réponse 4) : pendant un passage de l'agent (« en_cours »), relecture toutes les 3 s — Carl le voit ranger en direct
        // S232 lot D6 : et, dans les 20 s après la fin d'une réponse, jusqu'au passage du greffier de cet échange (relirePendantRangement)
        const d = data as DiscussionElements;
        let relire = d.suivi_agent?.rangement === "en_cours";
        if (finReponseRef.current !== null) {
          if (passageInitialRef.current === undefined) passageInitialRef.current = d.suivi_agent?.dernier_passage?.quand ?? null;
          relire = relire || relirePendantRangement(passageInitialRef.current, d, Date.now() - finReponseRef.current);
        }
        // P-S232-31 n° 1 : pendant un moment de la couche 3 (plan de match levé par un geste), relire toutes les 3 s, au plus 90 s
        if (d.suivi_agent?.moment_en_cours) { if (debutMomentRef.current === null) debutMomentRef.current = Date.now(); }
        else debutMomentRef.current = null;
        if (debutMomentRef.current !== null) relire = relire || relirePendantMoment(d, Date.now() - debutMomentRef.current);
        if (relire) {  // un seul minuteur : deux chaînes de relecture ne tournent plus en parallèle (après un geste et recharger)
          if (relectureRef.current) clearTimeout(relectureRef.current);
          relectureRef.current = setTimeout(() => { relectureRef.current = null; if (threadRef.current === externalId) fetchElements(externalId); }, RELECTURE_EN_COURS_MS);
        }
      }
    } catch (err) {
      console.warn("[useDiscussionElements] fetch error:", err);
      if (threadRef.current === externalId) setErreur(String((err as Error)?.message || err));
      if (!_isRetry && threadRef.current === externalId) {
        setTimeout(() => { if (threadRef.current === externalId) fetchElements(externalId, true); }, 3_000);
      }
    }
  }, []);

  // S231 lot B : chaque instance reçoit la réponse publiée pour son fil actif, quelle que soit l'instance qui l'a lue
  useEffect(() => abonnerElements((fil, data) => {
    filsLusRef.current.add(fil);
    if (threadRef.current === fil) { setEtat({ fil, data }); setErreur(null); }
  }), []);

  // (a) À l'ouverture ou au changement de discussion ; le même fil garde ses éléments pendant la relecture, un autre fil n'en montre aucun
  useEffect(() => {
    if (!activeThreadId) { setEtat(null); setErreur(null); return; }
    fetchElements(activeThreadId);
  }, [activeThreadId, fetchElements]);

  // (b) Quand isTyping passe de true à false (réponse terminée)
  // (c) Puis 20 s, 45 s et 90 s après — B1.MAQUETTE.E1 M1 (R-a) : le greffier range l'échange après la
  //     réponse ; constaté S223 (disc. 6448) : 37,6 s de rangement, une seule relecture à 20 s le manquait.
  useEffect(() => {
    const wasTyping = prevTypingRef.current;
    prevTypingRef.current = isTyping;

    if (wasTyping && !isTyping && activeThreadId) {
      finReponseRef.current = Date.now();  // S232 lot D6 : la fenêtre de relecture jusqu'au rangement commence
      passageInitialRef.current = undefined;
      fetchElements(activeThreadId);
      timersRef.current.forEach(clearTimeout);
      timersRef.current = RELECTURES_APRES_REPONSE_MS.map((delai) => setTimeout(() => {
        const tid = threadRef.current;
        if (tid) fetchElements(tid);
      }, delai));
    }

    return () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    };
  }, [isTyping, activeThreadId, fetchElements]);

  // ETAPES.AVANCEMENT.1 lot 3 : relecture immédiate après un geste (useGestesDiscussion), puis à 20 s (le greffier ne range pas un geste)
  const recharger = useCallback(() => {
    const tid = threadRef.current;
    if (tid) fetchElements(tid);
  }, [fetchElements]);

  // DEC-GREFFIER-AGENT-3 (règle C) : un geste fait dans une zone (workspace ou chat) fait relire l'autre — options du chat et boîtes à jour
  useEffect(() => {
    const relire = () => recharger();
    window.addEventListener(EVT_RELIRE_ELEMENTS, relire);
    return () => window.removeEventListener(EVT_RELIRE_ELEMENTS, relire);
  }, [recharger]);

  const elements = etat && etat.fil === activeThreadId ? etat.data : null;
  return { elements, erreur, recharger };
}
